import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Building2,
  Send,
  MessageSquare,
  CheckCircle,
  AlertTriangle,
  Image,
  X,
  Loader2,
  Clock
} from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { API_URL } from '../utils/config';
import { openAttachment } from '../utils/openAttachment';


const PropertyTicketReply = () => {
  const { token } = useParams();
  const [ticketData, setTicketData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTicketData();
  }, [token]);

  const fetchTicketData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/property-ticket/${token}`);
      if (res.ok) {
        const data = await res.json();
        setTicketData(data);
      } else {
        setError('Link non valido o scaduto');
      }
    } catch (err) {
      setError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    files.slice(0, 5).forEach(file => {
      if (!file.type.startsWith('image/')) {
        toast.error('Solo immagini permesse');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Max 5MB per immagine');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setReplyAttachments(prev => [...prev.slice(0, 4), reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!replyContent.trim()) {
      toast.error('Scrivi un messaggio');
      return;
    }

    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/property-ticket/${token}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: replyContent,
          attachments: replyAttachments
        })
      });

      const data = await res.json();

      if (res.ok) {
        setSent(true);
        toast.success('Risposta inviata con successo!');
        setReplyContent('');
        setReplyAttachments([]);
        // Ricarica i messaggi
        fetchTicketData();
      } else {
        toast.error(data.detail || 'Errore nell\'invio');
      }
    } catch (err) {
      toast.error('Errore di connessione');
    } finally {
      setSending(false);
    }
  };

  const getSenderStyle = (senderType) => {
    switch (senderType) {
      case 'responsabile':
        return 'bg-blue-100 text-blue-800';
      case 'proprieta':
        return 'bg-green-600 text-white';
      case 'ufficio':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-slate-100 text-slate-800';
    }
  };

  const getSenderLabel = (senderType) => {
    switch (senderType) {
      case 'responsabile': return 'Responsabile';
      case 'proprieta': return 'Tu';
      case 'ufficio': return 'Ufficio HWsmart';
      default: return senderType;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertTriangle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Link Non Valido</h1>
          <p className="text-slate-600">{error}</p>
          <p className="text-sm text-slate-500 mt-4">
            Se hai ricevuto questo link via email, potrebbe essere scaduto.
            Contatta il responsabile per ricevere un nuovo link.
          </p>
        </Card>
      </div>
    );
  }

  if (ticketData?.status === 'chiuso') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Toaster position="top-center" richColors />
        <Card className="max-w-md w-full p-8 text-center">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-slate-800 mb-2">Ticket Chiuso</h1>
          <p className="text-slate-600">
            Questo ticket è stato chiuso. Non è più possibile inviare messaggi.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Toaster position="top-center" richColors />
      
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <Card className="p-6 mb-4">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">
                {ticketData?.property_name}
              </h1>
              <p className="text-slate-500 text-sm">Comunicazione con il Responsabile</p>
            </div>
          </div>
          
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="font-medium text-slate-700 flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              {ticketData?.subject}
            </p>
          </div>
        </Card>

        {/* Messages */}
        <Card className="mb-4 overflow-hidden">
          <div className="p-4 bg-slate-50 border-b">
            <h2 className="font-semibold text-slate-700 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Cronologia Messaggi
            </h2>
          </div>
          
          <div className="p-4 space-y-4 max-h-[400px] overflow-y-auto">
            {ticketData?.messages?.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.sender_type === 'proprieta' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] rounded-lg p-3 ${getSenderStyle(msg.sender_type)}`}>
                  <p className="text-xs font-medium mb-1 opacity-80">
                    {getSenderLabel(msg.sender_type)}
                  </p>
                  <p className="text-sm whitespace-pre-wrap break-words [overflow-wrap:anywhere]">{msg.content}</p>
                  {msg.attachments?.length > 0 && (
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {msg.attachments.map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          alt=""
                          className="h-20 w-20 object-cover rounded border cursor-pointer"
                          onClick={() => openAttachment(img)}
                        />
                      ))}
                    </div>
                  )}
                  <p className="text-xs mt-2 opacity-60">
                    {new Date(msg.sent_at).toLocaleString('it-IT', {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Reply Form */}
        <Card className="p-6">
          <h2 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Send className="h-4 w-4" />
            Rispondi al Responsabile
          </h2>

          {sent && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              <span className="text-green-700 text-sm">Messaggio inviato con successo!</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Textarea
                value={replyContent}
                onChange={(e) => setReplyContent(e.target.value)}
                placeholder="Scrivi la tua risposta..."
                rows={4}
                className="w-full"
                data-testid="property-reply-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Non includere nomi, numeri di telefono o email nel messaggio.
              </p>
            </div>

            {/* Attachments */}
            <div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                className="gap-2"
              >
                <Image className="h-4 w-4" />
                Allega Foto
              </Button>
              <p className="text-xs text-slate-500 mt-1">
                Max 5 foto. Le immagini non devono contenere dati personali.
              </p>
              
              {replyAttachments.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {replyAttachments.map((img, idx) => (
                    <div key={idx} className="relative">
                      <img
                        src={img}
                        alt=""
                        className="h-20 w-20 object-cover rounded border"
                      />
                      <button
                        type="button"
                        onClick={() => setReplyAttachments(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <Button
              type="submit"
              disabled={!replyContent.trim() || sending}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="property-send-btn"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Invia Risposta
            </Button>
          </form>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 mt-6">
          Questa comunicazione è gestita da HWsmart
        </p>
      </div>
    </div>
  );
};

export default PropertyTicketReply;
